import React, { useState } from 'react';
import API from '../services/api';

export function GiveShoutOutModal({ isOpen, onClose, teammates = [], onPosted }) {
  const [toUserIds, setToUserIds] = useState([]);
  const [message, setMessage] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [emoji, setEmoji] = useState('💜');
  const [durationDays, setDurationDays] = useState(5);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const reset = () => {
    setToUserIds([]); setMessage(''); setVisibility('public'); setEmoji('💜');
    setDurationDays(5); setError('');
  };

  const toggleRecipient = (id) =>
    setToUserIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const submit = async () => {
    if (toUserIds.length === 0 || !message.trim()) return;
    setSending(true); setError('');
    try {
      // Each selected teammate gets their own shout-out (own card + reactions).
      await API.post('/therapist/shout-outs', {
        toUserIds: toUserIds.map(Number), message: message.trim(), visibility, emoji, durationDays,
      });
      reset();
      if (onPosted) onPosted();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to post. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const canSend = toUserIds.length > 0 && message.trim() && !sending;

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <style>{SHOUT_GLOW_CSS}</style>
        <div style={header}>
          <h3 style={{ margin: 0, color: '#fff', fontSize: '1.1rem' }}>{emoji} Give a shout-out</h3>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>
        <div style={body}>
          <label style={label}>To <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— tap names to pick one or more</span></label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
            {teammates.map((t) => {
              const selected = toUserIds.includes(t.therapist_id);
              return (
                <button
                  type="button"
                  key={t.therapist_id}
                  onClick={() => toggleRecipient(t.therapist_id)}
                  style={selected ? toggleOn : toggleOff}
                >
                  {selected ? '✓ ' : ''}{t.first_name} {t.last_name}
                </button>
              );
            })}
          </div>
          {toUserIds.length > 1 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Each person gets their own shout-out with this message.
            </div>
          )}

          <label style={label}>Message</label>
          <textarea
            value={message}
            maxLength={500}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            placeholder="What do you appreciate about them?"
            style={{ ...input, resize: 'vertical' }}
          />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'right' }}>{message.length}/500</div>

          <label style={label}>Pick an emoji <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— it glows on the shout-out</span></label>
          <div style={emojiPreviewRow}>
            <span className="shout-glow" style={{ fontSize: 30 }}>{emoji}</span>
          </div>
          <div style={emojiGridStyle}>
            {SHOUT_EMOJIS.map((e) => (
              <button
                type="button"
                key={e}
                onClick={() => setEmoji(e)}
                style={{ ...emojiBtnStyle, ...(emoji === e ? emojiBtnActiveStyle : {}) }}
              >
                {e}
              </button>
            ))}
          </div>

          <label style={label}>Who can see it?</label>
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={() => setVisibility('public')} style={visibility === 'public' ? toggleOn : toggleOff}>
              Everyone
            </button>
            <button type="button" onClick={() => setVisibility('private')} style={visibility === 'private' ? toggleOn : toggleOff}>
              🔒 Just them
            </button>
          </div>

          <label style={label}>Stays up for <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>— then it retires from the feed</span></label>
          <div style={durationRowStyle}>
            {[1, 2, 3, 4, 5, 6, 7].map((d) => (
              <button
                type="button"
                key={d}
                onClick={() => setDurationDays(d)}
                style={{ ...durationPillStyle, ...(durationDays === d ? durationPillActiveStyle : {}) }}
              >
                {d}
              </button>
            ))}
            <span style={{ fontSize: 13, color: 'var(--text-muted)', marginLeft: 4 }}>
              day{durationDays === 1 ? '' : 's'}
            </span>
          </div>

          {error && <p style={{ color: 'var(--error-color)', fontSize: 13, margin: '10px 0 0' }}>{error}</p>}

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <button onClick={submit} disabled={!canSend} style={{ ...primaryBtn, opacity: canSend ? 1 : 0.6 }}>
              {sending ? 'Sending…' : 'Send shout-out'}
            </button>
            <button onClick={onClose} style={secondaryBtn}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
};
const modal = {
  background: '#fff', borderRadius: 12, width: 'min(460px, 96vw)', maxHeight: '90vh',
  overflow: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)',
};
const header = {
  background: 'var(--primary-color)', padding: '14px 18px',
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
};
const closeBtn = { background: 'none', border: 'none', color: '#fff', fontSize: 24, cursor: 'pointer', lineHeight: 1 };
const body = { padding: '18px' };
const label = { display: 'block', fontWeight: 600, fontSize: 13, margin: '12px 0 4px' };
const input = {
  width: '100%', padding: '8px 10px', border: '1px solid var(--border-color)', borderRadius: 6,
  fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit',
};
const toggleBase = { flex: 1, padding: '8px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 };
const toggleOn = { ...toggleBase, background: 'var(--primary-color)', color: '#fff', border: '1px solid var(--primary-color)' };
const toggleOff = { ...toggleBase, background: '#fff', color: 'var(--text-strong)', border: '1px solid var(--border-color)' };
const primaryBtn = { background: 'var(--primary-color)', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 18px', fontWeight: 700, cursor: 'pointer' };
const secondaryBtn = { background: '#fff', color: 'var(--text-strong)', border: '1px solid var(--border-color)', borderRadius: 6, padding: '10px 16px', cursor: 'pointer' };
const emojiPreviewRow = { display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 8px' };
const emojiGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6, maxHeight: 132, overflowY: 'auto', padding: 2 };
const emojiBtnStyle = { fontSize: 18, lineHeight: 1, padding: '6px 0', borderRadius: 8, border: '1px solid var(--border-color)', background: '#fff', cursor: 'pointer' };
const emojiBtnActiveStyle = { border: '2px solid var(--accent-color)', background: '#f8efe8' };
const durationRowStyle = { display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' };
const durationPillStyle = {
  width: 34, height: 34, borderRadius: 8, border: '1px solid var(--border-color)',
  background: '#fff', color: 'var(--text-strong)', cursor: 'pointer', fontSize: 14, fontWeight: 600,
};
const durationPillActiveStyle = { border: '2px solid var(--accent-color)', background: 'var(--primary-color)', color: '#fff' };

// Short "winding down" label for the final stretch before a shout-out expires.
// Returns null when there's more than ~2 days left (no tag shown), so the card
// and feed stay uncluttered until a shout-out is about to retire.
export function expiryTagLabel(expiresAt) {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return 'Last day';
  const days = Math.floor(ms / 86400000); // full days remaining
  if (days === 0) return 'Last day';      // expires within 24h
  if (days === 1) return 'Expires tomorrow';
  if (days === 2) return 'Expires in 2 days';
  return null;
}

// Shared across the modal, hub feed, and dashboard card so the chosen emoji
// glows + gently pulses wherever a shout-out is shown.
export const SHOUT_EMOJIS = ['💜','❤️','🧡','💛','💚','💙','🤍','💖','✨','🌟','⭐','🔥','🎉','🎊','🙌','👏','👍','🤝','💪','🫶','🙏','🌈','☀️','🌻','🌸','🦄','🚀','💯','🏆','🥳','😎','🤩','🫡','🎈','🍀','☕'];
export const SHOUT_GLOW_CSS = `
@keyframes shoutPulse { 0%,100%{transform:scale(1);filter:drop-shadow(0 0 3px rgba(255,196,120,0.55));} 50%{transform:scale(1.12);filter:drop-shadow(0 0 12px rgba(255,196,120,0.95));} }
.shout-glow{display:inline-block;animation:shoutPulse 2.2s ease-in-out infinite;}
`;

// Emoji reactions on a shout-out. Shows a chip per emoji (count, highlighted if
// you reacted); the ＋ opens the palette to add one. `onReact(emoji)` toggles.
export function ReactionBar({ reactions = [], onReact }) {
  const [picking, setPicking] = useState(false);

  const chip = (mine) => ({
    display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13,
    padding: '1px 8px', borderRadius: 12, cursor: 'pointer',
    border: mine ? '1px solid var(--accent-color)' : '1px solid var(--border-color)',
    background: mine ? 'var(--pale-blue)' : 'var(--surface-muted)',
    color: 'var(--text-strong)',
  });

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {reactions.map((r) => (
          <button
            key={r.emoji}
            onClick={() => onReact(r.emoji)}
            title={r.mine ? 'Remove your reaction' : 'React'}
            style={chip(r.mine)}
          >
            <span>{r.emoji}</span>
            <span style={{ fontSize: 11, fontWeight: 600 }}>{r.count}</span>
          </button>
        ))}
        <button
          onClick={() => setPicking((p) => !p)}
          title="Add a reaction"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-strong)', padding: '2px 10px', borderRadius: 12, border: '1px solid var(--border-color)', background: 'var(--surface-muted)', cursor: 'pointer' }}
        >
          <span>😊</span><span>React</span>
        </button>
      </div>
      {picking && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, padding: 8, maxWidth: 300,
          background: '#fff', border: '1px solid var(--border-color)', borderRadius: 10,
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
        }}>
          {SHOUT_EMOJIS.map((e) => (
            <button
              key={e}
              onClick={() => { onReact(e); setPicking(false); }}
              style={{ fontSize: 18, background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1.3, padding: 2 }}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
